import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { agenciesAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './Agencies.css';

const Agencies = () => {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, suspended: 0 });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    page: 1,
    limit: 10
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const filterDropdownRef = useRef(null);
  const statusDropdownRefs = useRef({});

  // Activation Code Modal State
  const [activationModal, setActivationModal] = useState({
    isOpen: false,
    code: '',
    expiresAt: '',
    agencyName: ''
  });
  const [codeCopied, setCodeCopied] = useState(false);

  const fetchAgencies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await agenciesAPI.getAll(filters);
      if (response.data.success) {
        setAgencies(response.data.data.agencies);
        setPagination(response.data.data.pagination);
      }
    } catch (error) {
      console.error('Failed to fetch agencies:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = async () => {
    try {
      const response = await agenciesAPI.getStats();
      if (response.data.success) {
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, [fetchAgencies]);

  useEffect(() => {
    fetchStats();
  }, []);

  // Click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
      if (statusDropdownOpen !== null) {
        const currentRef = statusDropdownRefs.current[statusDropdownOpen];
        if (currentRef && !currentRef.contains(event.target)) {
          setStatusDropdownOpen(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownOpen]);

  const handleSearch = (e) => {
    setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }));
  };

  const handleStatusFilter = (status) => {
    setFilters(prev => ({ ...prev, status: prev.status === status ? '' : status, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setFilters(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const agency = agencies.find(a => a.id === id);
      const response = await agenciesAPI.updateStatus(id, { status: newStatus });

      // If activation code was generated (on approval), show modal
      if (response.data.data?.activationCode) {
        setActivationModal({
          isOpen: true,
          code: response.data.data.activationCode,
          expiresAt: response.data.data.activationCodeExpiresAt,
          agencyName: agency?.agency_name || 'Agency'
        });
        setCodeCopied(false);
      }

      fetchAgencies();
      fetchStats();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(activationModal.code);
    setCodeCopied(true);
    setTimeout(() => setCodeCopied(false), 2000);
  };

  const closeActivationModal = () => {
    setActivationModal({
      isOpen: false,
      code: '',
      expiresAt: '',
      agencyName: ''
    });
    setCodeCopied(false);
  };

  const formatExpiryDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusOptions = [
    { value: 'pending', label: 'PENDING', className: 'status-pending' },
    { value: 'approved', label: 'APPROVED', className: 'status-approved' },
    { value: 'rejected', label: 'REJECTED', className: 'status-rejected' },
    { value: 'suspended', label: 'SUSPENDED', className: 'status-suspended' }
  ];

  const filterOptions = [
    { value: '', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'suspended', label: 'Suspended' }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.value === status) || statusOptions[0];
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    return <span className={`status-badge ${config.className}`}>{config.label}</span>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (hours < 1) return 'JUST NOW';
    if (hours < 24) return `UPDATED ${hours}H AGO`;
    return `UPDATED ${days}D AGO`;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">TRAVEL AGENCIES</h1>
            <p className="page-subtitle">PARTNER MANAGEMENT CENTER</p>
          </div>
          <div className="header-right">
            <div className="search-box">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              <input
                type="text"
                placeholder="Search agencies..."
                value={filters.search}
                onChange={handleSearch}
              />
            </div>
            <div className="custom-dropdown" ref={filterDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              >
                <span>{filterOptions.find(opt => opt.value === filters.status)?.label || 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {filterDropdownOpen && (
                <div className="custom-dropdown-menu">
                  {filterOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`custom-dropdown-item ${filters.status === option.value ? 'active' : ''}`}
                      onClick={() => {
                        handleStatusFilter(option.value);
                        setFilterDropdownOpen(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={() => navigate('/agencies/new')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              ADD AGENCY
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="stats-grid stats-grid-5">
            <div
              className={`stat-card stat-card-clickable ${filters.status === '' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('')}
            >
              <div className="stat-info">
                <span className="stat-label">TOTAL AGENCIES</span>
                <span className="stat-value">{stats.total}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-primary stat-card-clickable ${filters.status === 'pending' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('pending')}
            >
              <div className="stat-info">
                <span className="stat-label">PENDING</span>
                <span className="stat-value">{stats.pending}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-success stat-card-clickable ${filters.status === 'approved' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('approved')}
            >
              <div className="stat-info">
                <span className="stat-label">APPROVED</span>
                <span className="stat-value">{stats.approved}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-error stat-card-clickable ${filters.status === 'rejected' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('rejected')}
            >
              <div className="stat-info">
                <span className="stat-label">REJECTED</span>
                <span className="stat-value">{stats.rejected}</span>
              </div>
            </div>
            <div
              className={`stat-card stat-warning stat-card-clickable ${filters.status === 'suspended' ? 'stat-card-active' : ''}`}
              onClick={() => handleStatusFilter('suspended')}
            >
              <div className="stat-info">
                <span className="stat-label">SUSPENDED</span>
                <span className="stat-value">{stats.suspended}</span>
              </div>
            </div>
          </div>

          {/* Agencies Table */}
          <div className="dashboard-section agencies-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading agencies...</p>
              </div>
            ) : agencies.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
                <p>No agencies found</p>
              </div>
            ) : (
              <>
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>AGENCY & CODE</th>
                        <th>CONTACT INFO</th>
                        <th>LOCATION</th>
                        <th>STATUS</th>
                        <th>ACTIONS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agencies.map((agency) => (
                        <tr key={agency.id}>
                          <td>
                            <div className="agency-info">
                              <div className="agency-avatar">
                                {agency.agency_name.charAt(0).toUpperCase()}
                              </div>
                              <div className="agency-details">
                                <span className="agency-name">{agency.agency_name}</span>
                                <span className="agency-code">{agency.agency_code}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="contact-info">
                              <span className="contact-email">{agency.email}</span>
                              {agency.phone_number && (
                                <span className="contact-phone">
                                  {agency.phone_country_code} {agency.phone_number}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="location-text">
                              {[agency.city, agency.state_province, agency.country]
                                .filter(Boolean)
                                .join(', ') || '-'}
                            </span>
                          </td>
                          <td>
                            <div className="status-cell">
                              <div
                                className="status-custom-dropdown"
                                ref={el => statusDropdownRefs.current[agency.id] = el}
                              >
                                <button
                                  className={`status-dropdown-trigger status-dropdown-${agency.status}`}
                                  onClick={(e) => {
                                    if (statusDropdownOpen === agency.id) {
                                      setStatusDropdownOpen(null);
                                    } else {
                                      const rect = e.currentTarget.getBoundingClientRect();
                                      setDropdownPosition({
                                        top: rect.bottom + 4,
                                        left: rect.left
                                      });
                                      setStatusDropdownOpen(agency.id);
                                    }
                                  }}
                                >
                                  <span>{getStatusConfig(agency.status).label}</span>
                                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                    <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                                {statusDropdownOpen === agency.id && (
                                  <div
                                    className="status-dropdown-menu"
                                    style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
                                  >
                                    {statusOptions.map((option) => (
                                      <div
                                        key={option.value}
                                        className={`status-dropdown-item ${agency.status === option.value ? 'active' : ''}`}
                                        onClick={() => {
                                          handleStatusChange(agency.id, option.value);
                                          setStatusDropdownOpen(null);
                                        }}
                                      >
                                        {option.label}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <span className="status-time">{formatDate(agency.updated_at)}</span>
                            </div>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <button
                                className="btn-view-agency"
                                onClick={() => navigate(`/agencies/${agency.id}`)}
                              >
                                VIEW
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="table-footer">
                  <div className="pagination-info">
                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                    {pagination.total} agencies
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </button>
                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.page <= 3) {
                        pageNum = i + 1;
                      } else if (pagination.page >= pagination.totalPages - 2) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                    <button
                      className="pagination-btn"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </main>

      {/* Activation Code Modal */}
      {activationModal.isOpen && (
        <div className="modal-overlay" onClick={closeActivationModal}>
          <div className="modal-content activation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-icon success">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
              <h2 className="modal-title">Agency Approved Successfully</h2>
              <p className="modal-subtitle">{activationModal.agencyName}</p>
            </div>

            <div className="modal-body">
              <div className="activation-code-section">
                <label className="code-label">Activation Code</label>
                <div className="code-display">
                  <span className="code-value">{activationModal.code}</span>
                  <button
                    className={`btn-copy ${codeCopied ? 'copied' : ''}`}
                    onClick={handleCopyCode}
                  >
                    {codeCopied ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Copied
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="code-warning">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>This code will only be shown once. Please copy and share with the agency.</span>
              </div>

              <div className="code-expiry">
                <span className="expiry-label">Expires:</span>
                <span className="expiry-value">{formatExpiryDate(activationModal.expiresAt)}</span>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-modal-close" onClick={closeActivationModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agencies;
